'use client';

import { Check, Sparkles } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';

import type { SalaryInsightsProfession } from '../types';

import styles from '../salary-insights.module.css';

import { FaqSection } from './FaqSection';
import { PromoBox } from './PromoBox';
import { ProfessionSeoSections } from './SalarySeoSections';
import { SalaryEstimator } from './SalaryEstimator';
import { SalaryCards } from './SalaryCards';
import { ShareLinks } from './ShareLinks';

interface SalaryInsightsProfessionPageProps {
  profession: SalaryInsightsProfession;
}

export function SalaryInsightsProfessionPage({ profession }: SalaryInsightsProfessionPageProps) {
  const [salary, setSalary] = useState<string>(profession.estimator.initialRange);

  const countryTableRows = Object.values(profession.salaryData).map((data) => [
    `<strong>${data.label}</strong>`,
    data.currency,
    `<span class="font-semibold text-slate-900">${data.ranges.mid}</span>`,
  ]);

  const experienceTableRows = profession.experienceTableRows.map((row) => [
    `<strong>${row.label}</strong>`,
    row.years,
    `<span class="font-semibold text-slate-900">${row.range}</span>`,
  ]);

  return (
    <main className="min-h-screen bg-white">
      {/* Hero banner */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#e0f7fa] via-white to-[#f0fdfa] pb-28 pt-20 text-slate-900 md:pb-32 md:pt-28">
        <div
          aria-hidden="true"
          className={`${styles.heroBlobLight} -left-20 -top-20 h-72 w-72 bg-[#3bbdad]/20 md:h-96 md:w-96`}
        />
        <div
          aria-hidden="true"
          className={`${styles.heroBlobLight} -bottom-32 -right-16 h-80 w-80 bg-[#0ea5e9]/15 md:h-[28rem] md:w-[28rem]`}
        />
        <div
          aria-hidden="true"
          className="absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 rounded-full bg-[#3bbdad]/10 blur-3xl"
        />

        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#3bbdad]/20 bg-white/70 px-4 py-1.5 text-sm font-semibold text-[#0f766e] shadow-sm backdrop-blur-sm">
            <Sparkles className="h-4 w-4 text-[#3bbdad]" />
            2026 Compensation Data
          </span>
          <h1 className="mb-6 text-balance text-4xl font-extrabold leading-tight tracking-tight md:text-5xl lg:text-6xl">
            {profession.h1}
          </h1>
          <p className="text-balance text-lg leading-relaxed text-slate-600 md:text-xl">
            {profession.intro}
          </p>
        </div>
      </section>

      {/* Estimator */}
      <section className="relative bg-slate-50 pb-16 md:pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SalaryEstimator profession={profession} onSalaryChange={setSalary} />
          <div className="mx-auto mt-8 max-w-3xl">
            <ShareLinks textTemplate={profession.share.textTemplate} salary={salary} />
          </div>
        </div>
      </section>

      {/* Tables and skills */}
      <section className="bg-white py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <h2 className="mb-3 text-2xl font-bold text-slate-900 md:text-4xl">
              {profession.countryTableCaption}
            </h2>
            <p className="text-lg text-slate-600">{profession.countryTableIntro}</p>
          </div>

          <div className="grid items-start gap-10 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <SalaryCards
                caption="Average Salary by Country (Mid-Level)"
                rows={countryTableRows}
              />
              <SalaryCards
                caption="Average Salary by Experience (United States)"
                rows={experienceTableRows}
              />
            </div>

            <aside className="space-y-6 lg:sticky lg:top-24">
              <div className="rounded-2xl border border-[#3bbdad]/20 bg-gradient-to-br from-[#f0fdfa] to-white p-6 shadow-sm">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-[#3bbdad]/10 text-[#3bbdad]">
                  <Sparkles className="h-5 w-5" />
                </div>
                <h3 className="mb-3 text-lg font-bold text-slate-900 md:text-xl">
                  {profession.skillsBox.title}
                </h3>
                <p className="mb-5 text-slate-600">{profession.skillsBox.description}</p>
                <ul className="mb-6 space-y-3">
                  {profession.skillsBox.skills.map((skill) => (
                    <li key={skill} className="flex items-start gap-3 text-slate-700">
                      <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#3bbdad]/10 text-[#3bbdad]">
                        <Check className="h-3 w-3" />
                      </span>
                      {skill}
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  className="h-auto w-full rounded-xl bg-slate-900 py-3 text-base font-semibold text-white shadow-md transition-transform hover:-translate-y-0.5 hover:bg-slate-800"
                >
                  <a href={profession.skillsBox.ctaLink}>{profession.skillsBox.ctaLabel}</a>
                </Button>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                <h3 className="mb-3 text-lg font-bold text-slate-900 md:text-xl">
                  {profession.underpaidBox.title}
                </h3>
                <p className="mb-5 text-slate-600">{profession.underpaidBox.description}</p>
                <Button
                  asChild
                  className="h-auto w-full rounded-xl bg-[#3bbdad] py-3 text-base font-semibold text-white shadow-md shadow-[#3bbdad]/20 transition-transform hover:-translate-y-0.5 hover:bg-[#3bbdad]/90"
                >
                  <a href="/salary-insights-report">
                    Get My Salary Report
                  </a>
                </Button>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <ProfessionSeoSections
        professionLabel={profession.breadcrumbLabel}
        professionSlug={profession.slug}
      />
      <PromoBox />
      <FaqSection faq={profession.faq} />
    </main>
  );
}
